/**
 * Formats a timestamp as a human-readable relative time string (e.g. "5m",
 * "2h", or "3 days ago"), relative to a given "now" reference.
 *
 * @param {string|number|Date} publishedAt - The date/time to format
 * @param {number|{value: number}} now - The current time (ms epoch, or a ref-like object with `.value`)
 * @param {boolean} [isCompact=false] - Whether to use compact units (e.g. "3d", "2mo", "1y") instead of Intl.RelativeTimeFormat phrasing
 * @returns {string} The relative time string, or "Invalid date" if publishedAt cannot be parsed
 */
export const getRelativeTime = (publishedAt, now, isCompact = false) => {
    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    const publishedDate = new Date(publishedAt);

    if (isNaN(publishedDate.getTime())) {
        console.error("Invalid date format:", publishedAt);
        return "Invalid date";
    }

    const nowMs = typeof now === 'number' ? now : Number(now?.value ?? Date.now());
    const seconds = Math.floor((nowMs - publishedDate.getTime()) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) {
        return "now";
    } else if (minutes < 60) {
        return `${minutes}m`;
    } else if (hours < 24) {
        return `${hours}h`;
    } else if (days < 30) {
        return isCompact ? `${days}d` : rtf.format(-days, "day");
    } else if (months < 12) {
        return isCompact ? `${months}mo` : rtf.format(-months, "month");
    } else {
        return isCompact ? `${years}y` : rtf.format(-years, "year");
    }
};
